import { useState } from "react";
import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useTransferOwner, type PreviousOwner } from "@/features/jobs/properties";
import { supabase } from "@/lib/supabase";
import { snapshotOfLines, type Line } from "./contactLines";

/**
 * The robust owner transfer (the home sold; the new owner called). Captures the new owner's
 * FULL identity; the old owner becomes a frozen, viewable Previous-owner record; the home's
 * equipment / warranty / access / history carry to the new owner; the handoff date attributes
 * every record old-vs-new. See research/property-ownership-lifecycle.md §8-correction.
 */
export function TransferOwnerModal({
  custno, locno, oldOwnerName, oldOwnerLines, onClose, onDone,
}: {
  custno: string; locno: string | null; oldOwnerName: string;
  /** Every line we call today (Pegasus contacts + ESC grid / phone bank) — frozen onto the previous owner on a sale. */
  oldOwnerLines?: Line[];
  onClose: () => void; onDone: () => void;
}) {
  const transfer = useTransferOwner();
  const [kind, setKind] = useState<"sale" | "tenant">("sale");
  // Editable (Seth 2026-08-21, the Jetto sale): ESC is usually RENAMED to the
  // buyer before this modal opens, so prefilling the "previous owner" from the
  // account name records the sale as buyer-sold-to-buyer. The office confirms
  // or corrects who actually SOLD.
  const [prevName, setPrevName] = useState(oldOwnerName);
  const [name, setName] = useState("");
  const [phone1, setPhone1] = useState(""); const [phone2, setPhone2] = useState("");
  const [email1, setEmail1] = useState(""); const [email2, setEmail2] = useState("");
  const [notes, setNotes] = useState("");
  const isSale = kind === "sale";

  const sameName = name.trim().length > 0 &&
    prevName.trim().toLowerCase() === name.trim().toLowerCase();
  const canSave = name.trim().length > 0 && !transfer.isPending &&
    (!isSale || (prevName.trim().length > 0 && !sameName));
  const submit = () => {
    transfer.mutate({
      custno, locno, oldOwnerName: isSale ? prevName.trim() : oldOwnerName, kind,
      oldOwnerLines: isSale && oldOwnerLines?.length ? snapshotOfLines(oldOwnerLines) : null,
      newOwner: {
        name, phone1: phone1 || null, phone2: phone2 || null,
        email1: email1 || null, email2: email2 || null, notes: notes || null,
      },
    }, { onSuccess: async () => {
      // On a SALE, kill the old owner's portal link immediately — a previous
      // homeowner must never reach the new owner's account (Seth, ownership
      // safety). A tenant change leaves the owner's account (and link) intact.
      if (isSale) {
        try { await supabase.rpc("rotate_customer_link_token", { p_custno: custno }); } catch { /* non-blocking */ }
      }
      onDone();
    } });
  };

  return (
    <Modal open onClose={onClose} title={isSale ? "New owner" : "New tenant"} subtitle={isSale ? "The home sold — transfer the account" : "Rental turnover — the owner keeps billing"} size="md">
      <div className="space-y-4">
        {/* What kind of change — a sale moves the whole account; a tenant change
            keeps the owner as biller and only swaps the occupant. */}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setKind("sale")}
            className={`rounded-lg border px-3 py-2 text-left transition-colors ${isSale ? "border-brand-400 bg-brand-50/60 ring-1 ring-brand-300" : "border-hairline hover:bg-slate-50"}`}>
            <span className="block text-xs font-semibold text-slate-800">Home sold</span>
            <span className="block text-[11px] leading-snug text-slate-500">New owner — archive the old owner</span>
          </button>
          <button type="button" onClick={() => setKind("tenant")}
            className={`rounded-lg border px-3 py-2 text-left transition-colors ${!isSale ? "border-brand-400 bg-brand-50/60 ring-1 ring-brand-300" : "border-hairline hover:bg-slate-50"}`}>
            <span className="block text-xs font-semibold text-slate-800">New tenant</span>
            <span className="block text-[11px] leading-snug text-slate-500">Owner keeps billing — occupant only</span>
          </button>
        </div>

        {isSale && (
          <OField label="Previous Owner — who SOLD the house *">
            <input value={prevName} onChange={(e) => setPrevName(e.target.value)} className="oinp w-full" placeholder="e.g. Ben & Wendy Caduff" />
            <span className="mt-1 block text-[11px] leading-snug text-slate-400">
              If ESC was already renamed to the buyer, the name above will be WRONG — type the sellers&apos; names. All the old history gets attributed to them.
            </span>
          </OField>
        )}
        {sameName && isSale && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
            Previous owner and new owner are the same name — that records the home as sold to itself. Fix whichever one is wrong.
          </p>
        )}
        {isSale ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs leading-relaxed text-amber-800">
            <span className="font-semibold">{prevName.trim() || oldOwnerName}</span> becomes the previous owner — their record (notes, money, agreements, messages) is <span className="font-semibold">archived and stays viewable</span>, never deleted. The home’s equipment, warranty, access &amp; service history carry to the new owner; the old owner's access notes archive (carry house facts like a gate code forward).
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
            <span className="font-semibold">{oldOwnerName}</span> stays the owner and biller — agreements, invoices and the portal link are untouched. This just records who's living there now alongside the owner.
          </div>
        )}

        <OField label={isSale ? "New owner name *" : "New tenant name *"}>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus className="oinp w-full" placeholder="e.g. Jane & John Buyer" />
        </OField>
        <div className="grid grid-cols-2 gap-3">
          <OField label="Phone"><input value={phone1} onChange={(e) => setPhone1(e.target.value)} className="oinp w-full" placeholder="612-555-0100" /></OField>
          <OField label="Phone (2nd)"><input value={phone2} onChange={(e) => setPhone2(e.target.value)} className="oinp w-full" placeholder="optional" /></OField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <OField label="Email"><input value={email1} onChange={(e) => setEmail1(e.target.value)} className="oinp w-full" placeholder="name@email.com" /></OField>
          <OField label="Email (2nd)"><input value={email2} onChange={(e) => setEmail2(e.target.value)} className="oinp w-full" placeholder="optional" /></OField>
        </div>
        <OField label="Notes (optional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="oinp w-full" placeholder='e.g. "bought the home in June 2026, referred by the Caduffs"' />
          <span className="mt-1 block text-[11px] leading-snug text-slate-400">
            Saved on the new owner&apos;s record and read by the AI snapshot — real dates or context you note here get used.
          </span>
        </OField>

        <div className="flex items-center justify-end gap-2 border-t border-hairline pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!canSave} onClick={submit}>
            <ArrowRight className="h-3.5 w-3.5" /> {transfer.isPending ? "Saving…" : isSale ? "Transfer to new owner" : "Record new tenant"}
          </Button>
        </div>
      </div>
      <style>{`.oinp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.875rem;font-family:inherit}.oinp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </Modal>
  );
}

function OField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

/** The archived previous owner(s) — kept viewable forever (Seth: "we still see the old info").
 *  Their money/agreements/messages stayed with them; the home's equipment + history moved on. */
export function PreviousOwnersModal({ previousOwners, escLines = [], onClose }: {
  previousOwners: PreviousOwner[];
  /** NEW-OWNER-CONTACTS: the lines ESC still carries for the address — the previous owner's, shown under the most recent one. */
  escLines?: Line[];
  onClose: () => void;
}) {
  return (
    <Modal open onClose={onClose} title="Previous Owners" subtitle="Archived — kept for history, never deleted" size="md">
      <div className="space-y-3">
        {previousOwners.length === 0 && <p className="text-sm text-slate-400">No previous owners recorded.</p>}
        {previousOwners.map(({ owner, ownership }, idx) => {
          const phones = [owner.phone1, owner.phone2].filter(Boolean).join(" · ");
          const emails = [owner.email1, owner.email2].filter(Boolean).join(" · ");
          const stillOnEsc = idx === 0 ? escLines : [];
          const bare = !phones && !emails && !owner.notes && stillOnEsc.length === 0;
          return (
            <div key={owner.id} className="rounded-lg border border-hairline p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-800">{owner.name}</span>
                <span className="text-[11px] text-slate-400">{ownerEra(ownership)}</span>
              </div>
              <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                {phones && <div>{phones}</div>}
                {emails && <div>{emails}</div>}
                {owner.notes && !owner.notes.startsWith("Contacts at handoff:") && <div className="text-slate-500">{owner.notes}</div>}
                {bare && <div className="text-slate-400">From the ESC account at handoff — contact details weren’t captured.</div>}
              </div>
              {stillOnEsc.length > 0 && (
                <div className="mt-2 rounded-md bg-slate-50 px-2.5 py-2">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">On the ESC account</div>
                  <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                    {stillOnEsc.map((l) => (
                      <li key={l.key} className="flex flex-wrap items-baseline gap-x-1.5">
                        {l.name && <span className="font-medium text-slate-800">{l.name}</span>}
                        {l.role && <span className="text-slate-400">{l.role}</span>}
                        {l.phone && <span className="tabular-nums">{prettyPhone(l.phone)}{l.ext ? ` x${l.ext}` : ""}</span>}
                        {l.email && <span className="truncate">{l.email}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-2 text-[11px] leading-snug text-slate-400">Their invoices, agreements &amp; messages stay with them; the home’s equipment &amp; history moved to the current owner.</p>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function prettyPhone(s: string) {
  const d = s.replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s;
}

function ownerEra(o: { start_date: string | null; end_date: string | null }) {
  // Parse the year straight off the ISO date string — new Date("2018-01-01") is UTC
  // midnight and getFullYear() in a negative-offset tz would slip to the prior year.
  const y = (d: string | null) => (d ? Number(d.slice(0, 4)) : null);
  const s = y(o.start_date), e = y(o.end_date);
  if (s && e) return s === e ? `owner ${s}` : `owner ${s}–${e}`;
  if (e) return `until ${e}`;
  return "previous owner";
}
